import { FileSignature } from 'lucide-react';
import { Link } from 'react-router';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../api/client.js';
import type { Smlouva, Zakazka } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import { formatKc } from '../lib/format.js';
import { SMLOUVA_TYP_LABEL } from '../lib/labels.js';
import { Badge, Btn, Chyba, EmptyState, Karta, SkeletonSeznam, td, th } from '../components/ui.js';
import { useNavigate } from 'react-router';

/** Přehled všech smluv firmy — odkazy na detail; nová smlouva se zakládá ze zakázky. */
export function SmlouvyPage() {
  const { maFinance, maPravo } = useAuth();
  const navigate = useNavigate();

  const {
    data: smlouvy,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['smlouvy'],
    queryFn: () => api.get<Smlouva[]>('/api/contracts'),
  });
  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
    enabled: maPravo('projekty.read'),
  });
  // Seznam se řídí globálním přepínačem zakázky; „všechny" na jedno kliknutí.
  const { vybranaId } = useVybranaZakazka(zakazky);
  const [vsechny, setVsechny] = useState(false);
  useEffect(() => setVsechny(false), [vybranaId]);
  const zobrazene =
    vsechny || !vybranaId ? smlouvy : smlouvy?.filter((x) => x.zakazkaId === vybranaId);
  const skryto = (smlouvy?.length ?? 0) - (zobrazene?.length ?? 0);

  const nazevZakazky = (id: string) => {
    const z = zakazky?.find((z) => z.id === id);
    return z ? `${z.cislo} — ${z.nazev}` : '—';
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Smlouvy</h1>
        {maPravo('smlouvy.write') && (
          <Btn size="desktop" onClick={() => void navigate('/smlouvy/nova')}>
            Nová smlouva
          </Btn>
        )}
      </div>
      <p className="text-sm text-text-secondary">
        Smlouva se váže na zakázku — vyberete ji v dalším kroku.
      </p>
      <Karta>
        {isLoading && <SkeletonSeznam radku={4} />}
        {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
        {zobrazene && zobrazene.length === 0 && (
          <EmptyState
            ikona={<FileSignature size={22} strokeWidth={1.75} />}
            titulek={skryto > 0 ? 'Vybraná zakázka nemá smlouvy' : 'Zatím žádné smlouvy'}
            popis="Začněte tlačítkem „Nová smlouva“ — bez hlavní SOD nejde importovat soupis prací."
            akce={
              skryto > 0 ? (
                <button
                  type="button"
                  className="font-semibold text-primary underline underline-offset-2"
                  onClick={() => setVsechny(true)}
                >
                  Zobrazit všechny zakázky ({skryto})
                </button>
              ) : undefined
            }
          />
        )}
        {zobrazene && zobrazene.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Typ</th>
                  <th className={th}>Číslo</th>
                  <th className={th}>Protistrana</th>
                  <th className={th}>Zakázka</th>
                  {maFinance && <th className={th}>Cena (bez DPH)</th>}
                </tr>
              </thead>
              <tbody>
                {zobrazene!.map((s) => (
                  <tr
                    key={s.id}
                    className="cursor-pointer hover:bg-bg"
                    onClick={() => void navigate(`/smlouvy/${s.id}`)}
                  >
                    <td className={td}>
                      <Badge stav={s.typ === 'hlavni' ? 'info' : 'neutral'}>
                        {SMLOUVA_TYP_LABEL[s.typ]}
                      </Badge>
                    </td>
                    <td className={td}>
                      <Link
                        to={`/smlouvy/${s.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-semibold text-primary underline underline-offset-2"
                      >
                        {s.cislo ?? '(bez čísla)'}
                      </Link>
                    </td>
                    <td className={td}>{s.protistrana}</td>
                    <td className={td}>{nazevZakazky(s.zakazkaId)}</td>
                    {maFinance && <td className={td}>{formatKc(s.cena)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {skryto > 0 && !vsechny && (
              <button
                type="button"
                className="mt-2 text-sm font-semibold text-primary underline underline-offset-2"
                onClick={() => setVsechny(true)}
              >
                + {skryto} na ostatních zakázkách — zobrazit všechny
              </button>
            )}
          </div>
        )}
      </Karta>
    </>
  );
}
